import { Injectable } from '@angular/core';
import { Selector, State, StateContext, Action, Store } from '@ngxs/store';
import { Borrower, PartnerRestrictedStatus } from 'app/models/dto/borrower.model';
import { PusherService } from 'app/modules/shared/services/pusher.service';
import { BorrowerService } from 'app/services/borrower/borrower.service';
import { catchError, tap, take } from 'rxjs/operators';
import { ClearBorrowerAlertsStore, CreateNewBorrowerAlert } from '../alerts/borrower-alerts.actions';
import { of, throwError } from 'rxjs';
import { GetBorrower, UpdateBorrower, UpdateBorrowerProfile, ClearBorrowerStore, PatchBorrower, GetBorrowerProfile, SubscribeToBorrower, UnsubscribeToBorrower, UpdateAssignedUserId, GetPartnerRestrictedStatus, GetBorrowerOwnerHistory, ShareBorrower, UnshareBorrower, ChangeBorrowerStatus, MoveBorrowerOutOfComplianceReview, ChangeBorrowerAssignment, RemoveApprovalsExperiment, GetERCInterest } from 'app/store/borrower/borrower/borrower.actions';
import { EventBusService } from 'app/modules/shared/services/event-bus.service';
import { DeleteBorrowerValueByAlias } from 'app/store/borrower-value/borrower-value.actions';
import { GlobalBorrowerTabUpdate } from 'app/store/global-borrower-tabs/global-borrower-tabs.actions';
import * as _ from 'lodash';
import { BorrowerStatusesState } from 'ng2-app/store/borrowerStatuses/borrower-statuses.state';
import { ERCInterest } from 'app/modules/borrower/models/erc-interest/erc-interest.model';

export class BorrowerStateModel {
  borrowerId: null | number;
  borrower: Borrower | null;
  borrowerProfile: [];
  borrowerValueHash?: {};
  ownerHistory: {};
  partnerRestrictedBorrowers: {
    [borrowerId: number]: PartnerRestrictedStatus;
  };
  applicationComplete: string | null;
  loading: boolean;
  error: string;
  ercInterest?: ERCInterest | any;
}

@State<BorrowerStateModel>({
	name: 'borrower',
	defaults: {
    borrowerId: null,
    borrower: null,
    borrowerProfile: [],
    borrowerValueHash: {},
    ownerHistory: [],
    partnerRestrictedBorrowers: {},
    applicationComplete: null,
    loading: false,
    error: '',
    ercInterest: {}
  }
})

@Injectable()

export class BorrowerState {

  @Selector()
  static borrower(state: BorrowerStateModel) {
    return state.borrower;
  }

  @Selector()
  static borrowerId(state: BorrowerStateModel) {
    return state.borrower?.id;
  }

  @Selector()
  static borrowerProfile(state: BorrowerStateModel) {
    return state.borrowerProfile;
  }

  @Selector()
  static borrowerValueHash(state: BorrowerStateModel) {
    return state.borrowerValueHash;
  }

  @Selector()
  static ownerHistory(state: BorrowerStateModel) {
    return state.ownerHistory;
  }

  @Selector()
  static applicationComplete(state: BorrowerStateModel) {
    return state.applicationComplete;
  }

  @Selector()
  static partnerRestrictedBorrower(state: BorrowerStateModel) {
    return (borrowerId: number) => state.partnerRestrictedBorrowers[borrowerId];
  }

  @Selector()
  static error(state: BorrowerStateModel) {
    return state.error;
  }

  @Selector()
  static loading(state: BorrowerStateModel) {
    return state.loading;
  }

  @Selector()
  static ERCInterest(state: BorrowerStateModel) {
    return state.ercInterest;
  }

  constructor(
    private borrowerService: BorrowerService,
    private pusherService: PusherService,
    private store: Store,
    private eventBusService: EventBusService
  ) { }

  @Action(GetBorrower, { cancelUncompleted: true })
	getBorrower({ getState, patchState }: StateContext<BorrowerStateModel>,
              { borrowerId, refresh = false, addBorrowerTab = true }: GetBorrower
  ) {
    const prevBorrowerId = getState().borrowerId;

    if (prevBorrowerId !== borrowerId) {
      this.store.dispatch(new ClearBorrowerAlertsStore());
      patchState({
        borrowerId,
        borrower: null,
        borrowerProfile: [],
        borrowerValueHash: {},
        ownerHistory: [],
        partnerRestrictedBorrowers: {},
        loading: true,
        error: ''
      });
    } else if (!refresh) {
      return;
    } else {
      patchState({ loading: true, error: '' });
    }

    return this.borrowerService.get(borrowerId).pipe(tap(borrower => {
      const borrowerValueHash = _.keyBy(borrower.borrower_values, 'alias');
      patchState({ borrower, borrowerValueHash, loading: false, applicationComplete: borrower.applicationComplete });
      if(addBorrowerTab) {
        this.store.dispatch(new GlobalBorrowerTabUpdate({
          tabId: borrower.id,
          name: borrower.name,
          stage: borrower.stage
        }));
      }
    }), catchError( error => {
      patchState({ error });
        return of();
    }));
  }

  @Action(GetBorrowerProfile)
  getBorrowerProfile({ patchState }: StateContext<BorrowerStateModel>, { borrowerId }: GetBorrowerProfile) {
    return this.borrowerService.getBorrowerProfile(borrowerId).pipe(tap(borrowerProfile => {
      patchState({ borrowerProfile });
    }));
  }

  @Action(UpdateBorrower)
	updateBorrower({getState}: StateContext<BorrowerStateModel>, { borrowerId, updates }: UpdateBorrower) {
    return this.borrowerService.update(borrowerId, updates).pipe(tap(() => {
      const currentBorrowerId = getState().borrowerId;
      if (borrowerId === currentBorrowerId) {
        this.store.dispatch(new GetBorrower(borrowerId, true));
      }
    }));
  }

  @Action(UpdateBorrowerProfile)
  updateBorrowerProfile({ patchState }: StateContext<BorrowerStateModel>, { borrowerId, updates }: UpdateBorrowerProfile) {
    return this.borrowerService.updateProfile(borrowerId, updates).pipe(tap(() => {
      patchState({ borrowerProfile: updates });
    }));
  }

  @Action(ClearBorrowerStore)
  clearBorrowerStore({ patchState }: StateContext<BorrowerStateModel>, { }: ClearBorrowerStore) {
    patchState({ borrower: null, partnerRestrictedBorrowers: {} });
  }

  @Action(PatchBorrower)
  patchBorrower({ patchState }: StateContext<BorrowerStateModel>, { borrower }: PatchBorrower) {
    const borrowerCopy = JSON.parse(JSON.stringify(borrower));
    patchState({ borrower: borrowerCopy });
  }

  @Action(SubscribeToBorrower)
  subscribeToBorrower({ getState }: StateContext<BorrowerStateModel>, { borrowerId }: SubscribeToBorrower) {
    const prevBorrowerId = getState().borrowerId;

    if (prevBorrowerId && prevBorrowerId !== borrowerId) {
      this.pusherUnsubscribe(prevBorrowerId);
    }

    this.pusherSubscribe(borrowerId);

    this.store.dispatch(new GetBorrower(borrowerId, true));
  }

  @Action(UnsubscribeToBorrower)
  unSubscribeToBorrower({}: StateContext<BorrowerStateModel>, { borrowerId }: UnsubscribeToBorrower) {
    this.pusherUnsubscribe(borrowerId);
  }

  @Action(UpdateAssignedUserId)
  updateAssignedUserId({ patchState }: StateContext<BorrowerStateModel>, { borrowerId, updates }: UpdateAssignedUserId) {
    return this.borrowerService.assignToUserId(borrowerId, updates).pipe(tap(() => {
      patchState({ borrowerProfile: updates });
    }));
  }

  @Action(GetPartnerRestrictedStatus)
  getPartnerRestrictedStatus({ getState, patchState }: StateContext<BorrowerStateModel>, { borrowerId }: GetPartnerRestrictedStatus) {
    const partnerRestrictedBorrower = getState().partnerRestrictedBorrowers[borrowerId];
    if (partnerRestrictedBorrower) {
      return;
    }
    return this.borrowerService.getPartnerRestrictedStatus(borrowerId).pipe(tap(status => {
      const partnerRestrictedBorrowers = { ...getState().partnerRestrictedBorrowers };
      partnerRestrictedBorrowers[borrowerId] = status;
      patchState({ partnerRestrictedBorrowers });
    }));
  }

  @Action(GetBorrowerOwnerHistory)
  getBorrowerOwnerHistory({ patchState }: StateContext<BorrowerStateModel>, { borrowerId }: GetBorrowerOwnerHistory) {
    return this.borrowerService.getOwnershipHistory(borrowerId).pipe(tap(ownerHistory => {
      patchState({ ownerHistory });
    }));
  }

  @Action(ShareBorrower)
  shareBorrower({ getState, patchState }: StateContext<BorrowerStateModel>, { borrowerIds, userIds }: ShareBorrower) {
    return this.borrowerService.shareBorrower(borrowerIds, userIds).pipe(tap(() => {
      const borrower = { ...getState().borrower } as Borrower;
      borrower.sharedWith = borrower.sharedWith.concat(userIds);
      patchState({ borrower });
    }),
      catchError( error => {
        console.error(error);
        return of();
      })
    );
  }

  @Action(UnshareBorrower)
  unshareBorrower(
    { getState, patchState }: StateContext<BorrowerStateModel>,
    { borrowerIds, unsharedUserIds, removeFromQueue }: UnshareBorrower
  ) {
    if (!this.validateShareParams(borrowerIds, unsharedUserIds)) {
      return true;
    }
    // This optional parameter indicates that the unshare wants to take a borrower of the funding desk queue
    removeFromQueue = removeFromQueue || null;

    return this.borrowerService.unshareBorrower(borrowerIds, unsharedUserIds).pipe(tap(() => {
        const borrower = { ...getState().borrower } as Borrower;
        // keep user in sharedWith if user is not in the unshared ids
        borrower.sharedWith = borrower.sharedWith.filter(currentlySharedWithUser => !unsharedUserIds.includes(currentlySharedWithUser));
        patchState({ borrower });
      }),
      catchError( error => {
        console.error(error);
        return of();
      })
    );
  }

  @Action(ChangeBorrowerStatus)
  changeBorrowerStatus({ getState }: StateContext<BorrowerStateModel>, { borrowerId, status, reason, dncParams }: ChangeBorrowerStatus) {
    const attrs = {
      status,
      reason,
      dncParams,
    };

    const borrowerStatusOptions = this.store.selectSnapshot( BorrowerStatusesState.statusOptions );

    const option = borrowerStatusOptions.find(x => x.status === status);
    if (!option) {
      this.store.dispatch(new CreateNewBorrowerAlert({
        level: 'error',
        message: `Invalid borrower status "${status}"`,
        dismissDelay: 5000,
      }));
      return;
    }

    if (option.borrower_stage_lookup.stage === 'inactive') {
      Object.assign(attrs, {
        canText: status !== 'doNotContact' ? 1 : 0,
        otherReason: null,
        smsUnsubscribed: status === 'doNotContact' ? 1 : 0,
      });
    }

    return this.borrowerService.update(borrowerId, attrs)
      .pipe(
        catchError(error => {
          this.store.dispatch(new CreateNewBorrowerAlert({
            level: 'error',
            message: `Failed to move borrower to "${option.status}".`,
            dismissDelay: 5000,
          }));
          return throwError(() => error);
        }),
        tap(() => {
          this.eventBusService.publish(
            new this.eventBusService.types.BorrowerStatusChangedEvent(borrowerId, option.borrower_stage_lookup.stage)
          );

          const prevBorrowerId = getState().borrowerId;
          if (borrowerId === prevBorrowerId) {
            this.store.dispatch(new GetBorrower(borrowerId, true));
          }
        })
      );
  }

  @Action(MoveBorrowerOutOfComplianceReview)
  moveBorrowerOutOfComplianceReview({ getState }: StateContext<BorrowerStateModel>, { borrowerId }: MoveBorrowerOutOfComplianceReview) {
    const prevBorrowerId = getState().borrowerId;
    if (prevBorrowerId !== borrowerId) {
      return;
    }

    return this.store.dispatch(new DeleteBorrowerValueByAlias(borrowerId, 'complianceReview'))
      .pipe(
        catchError((err) => {
          this.store.dispatch(new CreateNewBorrowerAlert({
            level: 'error',
            message: 'Something went wrong. Please try again.',
            dismissDelay: 5000,
          }));
          return throwError(() => err);
        }),
        tap(() => {
          const reactivateMerchantUserId = 6788637;
          this.store.dispatch(new ChangeBorrowerAssignment(borrowerId, reactivateMerchantUserId));
        })
      );
  }

  @Action(ChangeBorrowerAssignment)
  changeAssignment({ getState }: StateContext<BorrowerStateModel>, { borrowerId, userId }: ChangeBorrowerAssignment) {
    const prevBorrowerId = getState().borrowerId;
    if (prevBorrowerId !== borrowerId) {
      return;
    }

    return this.store.dispatch(new UpdateBorrower(borrowerId, { assignedLenderId: userId }))
      .pipe(
        catchError((err) => {
          if (err.status === 403) {
            this.store.dispatch(new CreateNewBorrowerAlert({
              level: 'error',
              message: 'You do not have permission to re-assign borrower',
              dismissDelay: 5000,
            }));
          } else if (err === 'Embedded borrowers cannot be reassigned.') {
            this.store.dispatch(new CreateNewBorrowerAlert({
              level: 'error',
              message: err,
              dismissDelay: 5000,
            }));
          } else {
            this.store.dispatch(new CreateNewBorrowerAlert({
              level: 'error',
              message: 'Something went wrong. Please try again.',
              dismissDelay: 5000,
            }));
          }
          return throwError(() => err);
        }),
        tap(() => {
          this.store.dispatch(new CreateNewBorrowerAlert({
            level: 'success',
            message: 'Successfully Re-Assigned Borrower',
            dismissDelay: 5000,
          }));
        })
      );
  }

  @Action(RemoveApprovalsExperiment)
  removeApprovalsExperiment({}: StateContext<BorrowerStateModel>, { borrowerId, userId }: RemoveApprovalsExperiment) {
    this.borrowerService.removeBorrowerFromApprovalsExperiment(userId).pipe(
      take(1),
      catchError( error => {
        console.error(error);
        return of();
      }),
    ).subscribe(() => {
      this.store.dispatch(new GetBorrower(borrowerId, true));
    });
  }


  validateShareParams(borrowers, users) {
    return borrowers && borrowers.length > 0 && users && users.length > 0;
  }

  pusherSubscribe(borrowerId: number) {
    this.pusherService
      .subscribe(`borrower-${borrowerId}`)
      .bind_global((eventName: string) => {
        if (!eventName.startsWith('pusher:')) {
          this.store.dispatch(new GetBorrower(borrowerId, true));
        }
    });
  }

  pusherUnsubscribe(borrowerId: number) {
    this.pusherService.unsubscribe(`borrower-${borrowerId}`);
  }

  @Action(GetERCInterest)
  getERCInterest({ patchState }: StateContext<BorrowerStateModel>, { borrowerId }: GetERCInterest) {
    return this.borrowerService.getERCInterest(borrowerId)
      .pipe(
        take(1),
        catchError( error => {
          console.error(error);
          return of();
        })
      )
      .subscribe(appLastModifiedDate => {
        patchState({
          ercInterest: {
            appLastModifiedDate,
          }
        });
    });
  }
}
